import { IconExternalLink } from "@tabler/icons-react";
import { ButtonLink } from "components/button/ButtonLink";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { PowerBiReport } from "src/features/powerBiReports/api/powerBiReportsApi.js";
import { DashboardWidgetBase } from "./DashboardWidgetBase.js";

export type PowerBiWidgetState =
	| { kind: "loading" }
	/** Only shown to company admins — they are the ones who can connect. */
	| { kind: "notConnected" }
	| { kind: "setupIncomplete" }
	| { kind: "error" }
	| { kind: "ready"; reports: PowerBiReport[] };

const REPORTS_SHOWN = 5;

export const PowerBiWidgetView = ({
	state,
	pagePath,
}: {
	state: PowerBiWidgetState;
	/** The Power BI reports page, which also hosts the setup wizard. */
	pagePath: string;
}) => {
	const { t } = useTranslation();

	if (state.kind === "ready" && state.reports.length > 0) {
		return (
			<DashboardWidgetBase title={t("Power BI reports")} flush>
				<ul className="divide-y divide-gray-100">
					{state.reports.slice(0, REPORTS_SHOWN).map((report) => (
						<li key={report.id}>
							{report.webUrl ? (
								<a
									href={report.webUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center justify-between gap-2 px-6 py-2 text-sm hover:bg-gray-50"
								>
									<span className="min-w-0 truncate">
										<span className="text-gray-900">{report.name}</span>
										<span className="text-gray-500">
											{" "}
											· {report.workspaceName}
										</span>
									</span>
									<IconExternalLink
										aria-label={t("Opens in Power BI")}
										className="size-4 shrink-0 text-gray-500"
									/>
								</a>
							) : (
								<span className="block truncate px-6 py-2 text-sm text-gray-500">
									{report.name}
								</span>
							)}
						</li>
					))}
				</ul>
				<div className="px-6 py-3">
					<Link
						to={pagePath}
						className="text-sm font-medium text-primary-700 hover:underline"
					>
						{t("Show all ({{count}})", { count: state.reports.length })}
					</Link>
				</div>
			</DashboardWidgetBase>
		);
	}

	return (
		<DashboardWidgetBase title={t("Power BI reports")}>
			{state.kind === "loading" ? (
				<p className="text-sm text-gray-500">{t("Loading")}</p>
			) : state.kind === "notConnected" ? (
				<div className="flex flex-col items-start gap-3">
					<p className="text-sm text-gray-600">
						{t(
							"Connect your Power BI to see your reports here and open them straight from the portal.",
						)}
					</p>
					<ButtonLink to={pagePath}>{t("Connect Power BI")}</ButtonLink>
				</div>
			) : state.kind === "setupIncomplete" ? (
				<div className="flex flex-col items-start gap-3">
					<p className="text-sm text-gray-600">
						{t(
							"Power BI is connected, but a few steps in Power BI are left before your reports show up.",
						)}
					</p>
					<ButtonLink to={pagePath}>{t("Finish the setup")}</ButtonLink>
				</div>
			) : state.kind === "error" ? (
				<div className="flex flex-col items-start gap-3">
					<p className="text-sm text-gray-600">
						{t("Could not load Power BI reports.")}
					</p>
					<ButtonLink to={pagePath} variant="secondaryGray">
						{t("Open Power BI reports")}
					</ButtonLink>
				</div>
			) : (
				<p className="text-sm text-gray-600">
					{t(
						"No reports have been shared with Exopen in your Power BI workspaces yet.",
					)}
				</p>
			)}
		</DashboardWidgetBase>
	);
};
